import { takeEvery, all, call, put } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import { SweetAlertResult } from 'sweetalert2';
import apiService from 'app/bff/services/apiService';
import { AaGetcaptchaResp } from 'app/bff/models/aa/aaGetcaptcha';
import { YesNoCodesEnum } from 'app/bff/enums/yesNo';
import { UserIdentityTypesEnum } from 'app/bff/enums/user';
import { ROUTES } from 'app/core/router/paths';
import modalService from 'app/core/services/modalService';
import { ProcessRouteMatchesStep as GeneralProcessRouteMatchesStep } from 'app/features/Member/Register/General/types';
import { sagaBoundary } from 'app/store/service';
import { MEMBER_REGISTER_AUTH__FETCH_CAPTCHA, MEMBER_AUTH__REGISTER_MEMBER, RegisterMemberAction } from './types';
import { fetchCaptchaDoneAction, fetchMemberProfileDoneAction, saveAuthPageDataAction } from './actions';
import { setAccessibleStepAction as setGeneralAccessibleStepAction } from '../general/actions';
import commonService from 'app/core/services/commonService';
import { MbSigninResp } from 'app/bff/models/mb/mbSignin';
import { MbGetinfoResp } from 'app/bff/models/mb/mbGetinfo';
import { ModalNamesEnum } from 'app/core/enums/ui/modals';

/**
 * @description 取得圖形驗證碼
 */
function * fetchCaptcha () {
  const response: AaGetcaptchaResp = yield call(apiService.postAaGetcaptcha, {
    unsecure: null
  });
  if (response) yield put(fetchCaptchaDoneAction(response));
}

/**
 * @description 註冊會員流程
 */
function * registerMember (action: RegisterMemberAction) {
  const response: MbSigninResp = yield call(apiService.postMbSignin, {
    data: action.payload.data,
    captcha: action.payload.checkCaptchaArgs
  });
  if (response) {
    // 緩存「身分驗證」頁面資料
    yield put(saveAuthPageDataAction({
      ...action.payload.data,
      birthday: action.payload.data.birthday ? commonService.convertToTWDate(action.payload.data.birthday) : '',
      id: action.payload.data.memberId ?? '',
      captchaCode: action.payload.checkCaptchaArgs.captchaCode
    }));
    // 是網投會員：引導會員登入
    if (response.userType === UserIdentityTypesEnum.OIS || response.userType === UserIdentityTypesEnum.BH) {
      const result: SweetAlertResult = yield modalService.confirm(
        '您已是網路會員',
        `<p class="m-0 mt-1 w-100">您已是本公司網路會員，</p>
          <p class="m-0 w-100">請輸入您的會員帳號與密碼登入</p>`,
        '立即登入',
        '返回首頁'
      );
      if (result.value && result.isConfirmed) {
        // 開啟會員登入 Modal
        modalService.login(true);
      } else {
        // 導頁至首頁
        yield put(push(ROUTES.HOME__MAIN));
      }
    }
    // 非網投會員：一般註冊
    if (response.userType === UserIdentityTypesEnum.NA) {
      // 緩存會員資料
      const memberInfo: MbGetinfoResp = yield call(apiService.postMbGetinfo, { users: { ROC_ID: action.payload.data.memberId } });
      const decryptedMemberInfo = commonService.decryptMemberInfo(memberInfo);
      yield put(fetchMemberProfileDoneAction(decryptedMemberInfo));
      // 將「同意相關告知事項」(Step-1) 設為可訪問步驟
      yield put(setGeneralAccessibleStepAction(GeneralProcessRouteMatchesStep[ROUTES.MEMBER__REGISTER__GENERAL__CLAUSES]));
      // 導頁至「同意相關告知事項」(Step-1)
      yield put(push(ROUTES.MEMBER__REGISTER__GENERAL__CLAUSES));
    }
    // 非網投會員：啟用網路投保權限
    if (response.userType === UserIdentityTypesEnum.NS && response.activateOis === YesNoCodesEnum.Yes) {
      const result: SweetAlertResult = yield modalService.confirm('提醒', '您已是本公司網路會員，歡迎啟用網路投保權限', '開啟權限', '離開');
      if (result.value && result.isConfirmed) {
        // 開啟「網服會員身份驗證」modal
        modalService.trigger(ModalNamesEnum.MemberRegisterNSModal, true);
      } else {
        // 導頁至首頁
        yield put(push(ROUTES.HOME__MAIN));
      }
    }
  }
}

export default function * watchAuthSaga () {
  yield all([
    takeEvery(MEMBER_REGISTER_AUTH__FETCH_CAPTCHA, sagaBoundary(fetchCaptcha)),
    takeEvery(MEMBER_AUTH__REGISTER_MEMBER, sagaBoundary(registerMember))
  ]);
}
